import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import API, { ThenArg } from '../../services/API';
import { CloseIcon } from '../_helpers/Icon/Close';
import CartShippingRange from './CartShippingRange';
import { rem } from 'polished';
import { InputStyled } from '../_helpers/form/Input';
import { __ } from '@bart.sk-eshop/react-i18n';
import { ButtonStyledStyles } from '../_helpers/form/Button';
import { applyGiftcardOrCustomerCard } from '../../containers/Cart/actions';
import { TotalPriceCartComponentSide } from './TotalPriceCartComponentSide';
import { push } from 'react-router-redux';
import CartProductListSidebar from './CartProductListSidebar';
import CartProductOnDemandListSidebar from './CartProductOnDemandListSidebar';
import CartVoucherApplied from './CartVoucherApplied';
import { Loader } from '../_helpers/Loader/Loader';
import { ChematCustomerType } from '../../library/ChematCustomerType';
import Alert from '../Alert/Alert';

interface SideCartProps {
  open: boolean;
  onClose: () => void;
  cart: ThenArg<typeof API.getCart>;
  user: any;
  isOnlyDemand: boolean;
  dispatch: any;
  settings: any;
  error?: any;
  deliveryFreePrice: number;
}

export const SideCart: React.FC<SideCartProps> = ({
  open,
  onClose,
  cart,
  user,
  dispatch,
  settings,
  error,
  deliveryFreePrice,
}) => {
  const [loading, setLoading] = React.useState<boolean>(false);
  const [couponCode, setCouponCode] = React.useState<string>('');

  let onDemandCartItems: ThenArg<typeof API.getCart>['items'] = [];
  let regularCartItems: ThenArg<typeof API.getCart>['items'] = [];

  // Zablokuj scrollovanie pozadia
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    document.body.classList.toggle('side-cart-open', open);
    const smartsupp = (window as any).smartsupp;
    if (typeof smartsupp === 'function') {
      smartsupp(open ? 'chat:hide' : 'chat:show');
    }
    return () => {
      document.body.classList.remove('side-cart-open');
      if (typeof (window as any).smartsupp === 'function') {
        (window as any).smartsupp('chat:show');
      }
    };
  }, [open]);

  // ESC zavrie košík
  useEffect(() => {
    if (!open) return;
    const escHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', escHandler);
    return () => window.removeEventListener('keydown', escHandler);
  }, [open, onClose]);

  const submitCoupon = async () => {
    if (couponCode && couponCode !== '') {
      setLoading(true);
      await dispatch(applyGiftcardOrCustomerCard(couponCode));
      setLoading(false);
    }
  };

  const onGoToCart = async () => {
    dispatch(push('/kosik'));
  };

  // Split cart items
  if (cart && cart.items?.length > 0) {
    cart.items.forEach((cartItem) => {
      if ((cartItem.product as any)?.on_demand) {
        onDemandCartItems.push(cartItem);
      } else {
        regularCartItems.push(cartItem);
      }
    });
  }

  const isDemand: boolean =
    (!regularCartItems || regularCartItems.length === 0) &&
    onDemandCartItems.length > 0;

  const showCouponInput: boolean =
    !user || user.skvelko_type === ChematCustomerType.RETAIL;

  const isEmpty: boolean = !cart || !cart.items || cart.items.length === 0;

  return (
    <>
      <Overlay open={open} onClick={onClose} />
      <CartDrawer
        open={open}
        tabIndex={-1}
        aria-modal="true"
        role="dialog"
        className="force-mobile"
      >
        <Header>
          <h2>
            {' '}
            {isDemand ? __('Cenová kalkulácia') : __('Nákupný košík')}{' '}
            <CartCount>
              ({cart?.items?.length ? cart.items.length : 0})
            </CartCount>
          </h2>
          <button onClick={onClose} aria-label="Zavrieť košík">
            <CloseIcon />
          </button>
        </Header>

        <ProductsWrapper>
          {isEmpty ? (
            <EmptyWrapper>
              <EmptyText>{__('Váš košík je prázdny')}</EmptyText>
              <EmptyButton type="button" className="primary" onClick={onClose}>
                {__('Pokračovať v nákupe')}
              </EmptyButton>
            </EmptyWrapper>
          ) : null}
          {regularCartItems.length ? (
            <CartProductListSidebar
              items={regularCartItems}
              dispatch={dispatch}
              user={user}
              cart={cart}
              settings={settings}
              isMobile={true}
              onProductClick={onClose}
            />
          ) : (
            ''
          )}
          {onDemandCartItems.length ? (
            <CartProductOnDemandListSidebar
              items={onDemandCartItems}
              dispatch={dispatch}
              user={user}
              isOnlyDemand={isDemand}
              cart={cart}
              isMobile={true}
              onProductClick={onClose}
            />
          ) : (
            ''
          )}
        </ProductsWrapper>

        {error && error.details && error.details.description && (
          <ErrorAlertWrapper>
            <Alert type="error">{error.details.description}</Alert>
          </ErrorAlertWrapper>
        )}

        {isEmpty ? null : (
          <Footer>
            <FooterSplittedRow>
              <FooterSplittedColumn>
                <CartShippingRange
                  cart={cart}
                  user={user}
                  freeShippingPrice={deliveryFreePrice}
                  size="extra-small"
                  textType={showCouponInput ? 0 : 1}
                />
              </FooterSplittedColumn>
              {showCouponInput && (
                <FooterSplittedColumn>
                  <Loader loading={loading}>
                    <CouponWrapper>
                      <CartVoucherInput
                        name="voucher"
                        type="text"
                        placeholder={__('Kód kupónu / karty')}
                        onChange={(e) => {
                          setCouponCode(e.target.value);
                        }}
                      />
                      <CartVoucherSubmit
                        type="button"
                        onClick={() => submitCoupon()}
                      >
                        {__('Uplatniť')}
                      </CartVoucherSubmit>
                    </CouponWrapper>
                  </Loader>
                </FooterSplittedColumn>
              )}
            </FooterSplittedRow>
            <div>
              {cart &&
              ((cart.giftcards && cart.giftcards.length) ||
                !!cart.customer_card_discount) ? (
                <CartVoucherApplied cart={cart} />
              ) : (
                ''
              )}
            </div>
            <FooterSplittedRowWithBorder>
              <FooterSplittedColumn>
                <TotalPriceCartComponentSide
                  cart={cart}
                  dispatch={dispatch}
                  onDemandCartItems={onDemandCartItems}
                  regularCartItems={regularCartItems}
                  user={user}
                />
              </FooterSplittedColumn>
              <FooterSplittedColumn>
                <GoToCartButton onClick={() => onGoToCart()}>
                  {' '}
                  {__('Prejsť do košíka')}
                </GoToCartButton>
              </FooterSplittedColumn>
            </FooterSplittedRowWithBorder>
          </Footer>
        )}
      </CartDrawer>
    </>
  );
};

// ==== Animácia pre vnútorný obsah ====
const contentFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// ==== Overlay ====
const Overlay = styled.div<{ open: boolean }>`
  opacity: ${({ open }) => (open ? 1 : 0)};
  pointer-events: ${({ open }) => (open ? 'auto' : 'none')};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.35);
  z-index: 9998;
  transition: opacity 0.35s cubic-bezier(0.77, 0, 0.18, 1);
  backdrop-filter: ${({ open }) => (open ? 'blur(2px)' : 'blur(0)')};
`;

// ==== Cart Drawer ====
const CartDrawer = styled.div<{ open: boolean }>`
  position: fixed;
  right: 0;
  top: 0;
  /* Samotné 100vh na tablete/mobile siaha pod lištu prehliadača (skryje footer).
     Nenahrádzať bottom: 0 – containing block je transformovaný header, nie viewport. */
  height: 100vh;
  height: 100dvh;
  width: 450px;
  background: #fff;
  box-shadow: -6px 0 32px rgba(0, 0, 0, 0.13);
  z-index: 9999;
  will-change: transform;
  transform: ${({ open }) => (open ? 'translateX(0)' : 'translateX(100%)')};
  transition: transform 0.45s cubic-bezier(0.77, 0, 0.18, 1);
  display: flex;
  flex-direction: column;
  cursor: default;
`;

// ==== Header ====
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  border-bottom: 1px solid #e6e6e6;

  h2 {
    font-size: 1.1rem;
    font-weight: 600;
    margin: 0;
  }

  button {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #444;

    &:hover {
      opacity: 0.7;
    }
  }
`;

// ==== Products (scrollable middle section) ====
const ProductsWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  animation: ${contentFadeIn} 0.45s ease forwards;
`;

// ==== Prázdny košík ====
const EmptyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${rem(20)};
  padding: ${rem(48)} ${rem(20)};
  text-align: center;
`;

const EmptyText = styled.p`
  margin: 0;
  font-size: ${rem(16)};
  color: #333;
`;

const EmptyButton = styled.button`
  ${ButtonStyledStyles};
  cursor: pointer;
  height: ${rem(50)};
  padding: 0 ${rem(24)};
`;

// ==== Error Alert ====
const ErrorAlertWrapper = styled.div`
  position: relative;
  padding: ${rem(16)} ${rem(20)};
  background: #fff;
  border-top: 1px solid #e6e6e6;
`;

// ==== Footer ====
const Footer = styled.div`
  border-top: 1px solid #e6e6e6;
  background: #fafafa;

  .summary {
    font-size: 0.95rem;
    color: #333;
    margin-bottom: 12px;

    .green {
      color: #008a00;
      font-weight: 600;
    }

    .red {
      color: #d50000;
      font-weight: 700;
    }
  }

  button {
    width: 100%;
    padding: 12px 16px;
    background: #d50000;
    color: #fff;
    border: none;
    border-radius: 6px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.25s ease;

    &:hover {
      background: #b70000;
    }
  }
`;

const CartCount = styled.div`
  display: inline;
  font-weight: normal;
`;

const FooterSplittedRow = styled.div`
  display: flex;
  justify-content: space-around;
  padding: 16px 20px;
  gap: ${rem(12)};
`;

const FooterSplittedRowWithBorder = styled(FooterSplittedRow)`
  border-top: 1px solid #e6e6e6;
`;

const FooterSplittedColumn = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  width: 100%;

  &:only-child {
    justify-content: stretch;

    > * {
      width: 100%;
    }
  }
`;

const CouponWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  height: 100%;
`;

const CartVoucherInput = styled(InputStyled)`
  flex: 1 1 auto;
  e.targetmargin-right: ${rem(10)};
  min-width: ${rem(16)};
  width: 100%;
  margin-right: ${rem(10)};
`;

const CartVoucherSubmit = styled.div`
  flex: 0 0 auto;
  text-align: center;
  ${ButtonStyledStyles};
  padding: ${rem(2)};
  background-color: #f4f4f4;
  cursor: pointer;
`;

const GoToCartButton = styled.button`
  height: ${rem(50)};
`;
